<script setup lang="ts">
/**
 * /orders —— 运营端订单驾驶舱(v2 · O1 的旗舰页;S-42 按画板重排,深色)。
 *
 * ═══ 这一页能做什么(与 S-10 那版一字未变的能力)═══
 *
 * ① 看全部订单,按状态与业务线筛;「付了钱但没人接」(pending_accept)是 B8 兜底的那批
 * ② 代办单:办理中 →(填一句审批备注)点「已办完」→ 转「等接单」
 *    · 没点「已办完」就想直接转等接单 —— 服务端会拒,页面上会看到那句拒绝的理由
 *    · 审批备注**只进审计日志**:客户那边只有「办理中 / 已办完」两态(M-01)
 * ③ 无人抢单时人工指派承运商 —— 返回体里能看到佣金率快照(M-11)
 * ④ 每一步的痕都在抽屉里的「审计流水」里(actorId 是哪个运营账号)
 *
 * ═══ 四张统计卡的数字从哪来(这一页最容易撒谎的地方)═══
 *
 * 待指配 / 在途:服务端对该状态的**计数**(`GET /api/admin/orders?status=…` 的 total,
 *   只取一页一条 —— 契约里没有计数接口)。**不许数本页条数**:那一页最多 50 条,
 *   把本页的计数标成全量,演示时说出口就是一个错数字,而页面上完全看不出来。
 * 今日新增 / 待审承运商:契约里**没有数据源**(订单列表没有创建日期区间参数;
 *   入驻申请队列只有 page/pageSize,没有状态过滤也没有计数接口)—— 画「—」,
 *   不许拿别的口径顶替(见下面那两条 @unimplemented 与 assumptions 的 AS-087)。
 *
 * ═══ 与画板的三处已知差异(都在 design-copy 的 O1 帧里记着)═══
 *
 * ① 筛选条只有两个下拉:画板的「全部承运商」「近 7 天」在契约里没有对应参数
 *    (opsListOrders 只认 status / serviceType),做出来就得前端过滤一页 50 条 ——
 *    那是把「全量里的 12 条」显示成「共 12 单」。要它们先往契约加参数。
 * ② 抽屉里除了画板那三张卡(新字段 / 订单概览 / 审计流水),另有四张画板没画的:
 *    客户联系人与金额、运输段、代办推进、人工指派。前两张是换版前就在的信息
 *    (联系电话与合计构成在别处没有第二个落点),后两张是 M-01 / M-11 的**操作入口** ——
 *    画板那一屏画的是一张「运输中」的单,那两张卡对它是隐藏的。删掉它们等于把
 *    S-10 的验收链路从界面上摘掉。
 * ③ 状态标签的中文名与配色一律走 @sea-drone/ui 的单点表(statusLabel / StatusTag):
 *    画板上的「待指配」是统计卡的指标名(这一页自己写),表格行标签仍叫单点表里那个名字
 *    (见 assumptions 的 AS-088)。
 */
import { computed, h, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  NButton,
  NDrawer,
  NDrawerContent,
  NInput,
  NSelect,
  NSpace,
  useMessage,
  type DataTableColumns,
} from 'naive-ui'
import {
  DataTable,
  EmptyState,
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PageHeader,
  PanelCard,
  StatTile,
  StatusTag,
  anchorageScopeLabel,
  dispatchModeLabel,
  refundStatusLabel,
  serviceTypeLabel,
  statusLabel,
  transportModeLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import {
  ORDER_STATUS,
  SERVICE_TYPE,
  formatCentsYuan,
  formatRateBp,
  formatShanghaiMinute,
  type OrderStatus,
  type ServiceType,
} from '@sea-drone/shared'
import {
  api,
  ApiError,
  type AdminOrderDetail,
  type AuditLogItem,
  type Carrier,
} from '../api/client'

/** 这一页是运营端旗舰页,深色底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const message = useMessage()
const router = useRouter()

/**
 * 打开**完整详情页**(v2 · O9,S-47)。抽屉是「在列表里顺手看一眼」,那一页是
 * 「为这一单停下来看」:整块「飞行节点凭证」(四张照片 + 时刻)与航线示意卡
 * 只有那一页有。入口就这一枚 —— 详情页不进侧栏(Y-32)。
 */
function openFullDetail(orderNo: string): void {
  void router.push(`/orders/${encodeURIComponent(orderNo)}`)
}

const orders = ref<AdminOrderDetail[]>([])
/** 返回体里的 total —— 当前筛选条件下的**全量**单数,不是本页行数 */
const total = ref(0)
const carriers = ref<Carrier[]>([])
const loading = ref(false)
const selectedOrderNo = ref<string | null>(null)
const selectedOrder = ref<AdminOrderDetail | null>(null)
const orderAudit = ref<AuditLogItem[]>([])
const lastError = ref<string | null>(null)
const note = ref('')
const assignCarrierId = ref<string | null>(null)

const statusFilter = ref<OrderStatus | null>(null)
const serviceTypeFilter = ref<ServiceType | null>(null)

/**
 * 待指配 / 在途两个数:单状态轻请求取 total(见文件头那段)。
 * `null` 表示「还没取到或取失败」,渲染成「—」—— 与「本来就没有数据源」同形,
 * 但后者在页面上有一整张卡的注释与一条 @unimplemented 顶着,取失败时顶部的错误条同时会亮。
 */
const pendingAcceptTotal = ref<number | null>(null)
const inTransitTotal = ref<number | null>(null)

/** 审计流水里展开着明细的那一行(点一下展开、再点收起) */
const openAuditIndex = ref<number | null>(null)

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '看全部订单,按状态与业务线筛;「付了钱但没人接」(pending_accept)是 B8 兜底的那批。' +
      '代办单:办理中 →(填一句审批备注)点「已办完」→ 转「等接单」。' +
      '无人抢单时人工指派承运商,返回体里能看到佣金率快照。' +
      '每一步的痕都留在这一单的审计流水里(actorId 是哪个运营账号)。',
    refs: ['S-10', 'M-01', 'M-03', 'B8', 'M-11'],
  },
  {
    title: '「等接单」是哪些单',
    body: '「等接单」那批就是「付了钱但没人接」—— B8 的人工指派兜底为它们准备。',
    refs: ['B8'],
  },
  {
    title: '四张统计卡的数字从哪来',
    body:
      '「待指配」「在途」是服务端对这两个状态的**计数**(单状态轻请求取 total,不是本页条数)。' +
      '「今日新增」与「待审承运商」在契约里没有数据源:订单列表没有按创建日期过滤的参数,' +
      '入驻申请队列只有分页、没有状态过滤也没有计数接口 —— 所以画「—」。' +
      '拿别的口径顶替(比如本页里今天那几条、或者申请总数)是一句看起来完全正常的假话。',
    refs: ['S-42'],
  },
  {
    title: '一张表里有多少单',
    body:
      '表格按 createdAt 倒序取 50 条(契约里 listOrders 的默认 pageSize),' +
      '筛选条右端那句「共 N 单」是服务端的计数 —— 单量超过 50 时,更早的单要缩小筛选范围才看得到。',
    refs: [],
  },
]

/** 抽屉里每张卡自己的「口径说明」—— 挂在它说的那件事旁边,不堆在页头 */
const MONEY_NOTES: readonly ScopeNote[] = [
  {
    title: '金额显示为元、内部整数分',
    body:
      '实付与合计显示为元（¥，M-14 的存储口径仍是整数分，Y-59）；实付为空显示「未付」。' +
      '合计的分解是「运费 + 代办费」，两者也同样按元显示。',
    refs: ['M-14'],
  },
  {
    // 这句话原来挂在计价表页的「试算结果」那张卡上。试算面板按 N-07 撤掉之后,
    // 运营端还能看到逐项分解的地方只剩这里了,所以它跟着搬到这张卡上
    title: '「合计构成」这一行加起来恰好等于合计',
    body:
      '运费 + 代办费恰好等于合计,快照里的逐项明细之和也一样 —— 这条等式由' +
      'probes/invariants.mjs 里的「订单计价明细之和等于应付总额且调价不改历史单」每次都验一遍。',
    refs: ['M-11', 'M-14'],
  },
]

const AGENCY_NOTES: readonly ScopeNote[] = [
  {
    title: '备注去哪了、什么时候会被拒',
    body:
      '客户只看得到「办理中 / 已办完」两态，审批明细不向他展示。下面这一句备注只进审计日志。' +
      '没点「已办完」就想推到「等接单」，服务端会当场拒绝。',
    refs: ['M-01'],
  },
]

const ASSIGN_NOTES: readonly ScopeNote[] = [
  {
    title: '指派的语义',
    body: '只有停在「等接单」的单能指派。指派是一次成交：佣金率快照当场写下，之后不可回改（M-11）。',
    refs: ['B8', 'M-11'],
  },
]

const statusOptions = ORDER_STATUS.map((s) => ({ label: `${statusLabel(s)}(${s})`, value: s }))
const serviceTypeOptions = SERVICE_TYPE.map((s) => ({ label: serviceTypeLabel(s), value: s }))
const carrierOptions = computed(() =>
  carriers.value
    .filter((c) => c.enabled)
    // 佣金率给人看的是百分数(Y-59);存进库里的仍是整数 bp(M-14)
    .map((c) => ({ label: `${c.companyName}(${formatRateBp(c.commissionRateBp)})`, value: c.carrierId })),
)

/**
 * 画板的时间形态 `10-15 14:02`:共享包给的是 `2026-10-15 14:02`(东八区,Y-03),
 * 把年份切掉就是画板那一格。手写 toLocaleString 会在容器 TZ=UTC 时显示早 8 小时的时刻。
 */
function shortTime(iso: string): string {
  return formatShanghaiMinute(iso).slice(5)
}

/** 承运商 / 飞手为空时画「—」:空着读起来像「这里没有这一项」,它其实只是还没有值 */
function orDash(value: string | null): string {
  return value ?? '—'
}

/** 路线 = 起点区域 → 终点区域(契约 GeoPoint.area);区域没标注时画「—」 */
function routeOf(o: AdminOrderDetail): string {
  return `${orDash(o.origin.area)} → ${orDash(o.destination.area)}`
}

/** 抽屉的开合挂在选中单上 —— 关掉抽屉就是「不再看这一单」,不额外多一个状态 */
const drawerOpen = computed({
  get: () => selectedOrder.value !== null,
  set: (open: boolean) => {
    if (open) return
    selectedOrderNo.value = null
    selectedOrder.value = null
    orderAudit.value = []
    openAuditIndex.value = null
    // S-61:关抽屉等于「不再看这一单」—— 上一单的指派草稿与备注不能留到下一单
    assignCarrierId.value = null
    note.value = ''
  },
})

/**
 * 九列,逐列一的数据源(画板 O1 的表头逐字):
 *   单号 orderNo · 状态 status(StatusTag 走单点表)· 客户 contact.name ·
 *   路线 origin.area → destination.area · 承运商 carrierName(空「—」)·
 *   飞手 pilotName(空「—」,L0-01 —— 契约里有,换版前这一列没渲染)·
 *   金额 paidAmount(¥ + 整数分转字符串,M-14)· 下单时间 createdAt(东八区,到分钟)·
 *   操作 「详情」(打开抽屉)
 */
const columns = computed<DataTableColumns<AdminOrderDetail>>(() => [
  {
    title: '单号',
    key: 'orderNo',
    width: 170,
    render: (row) => h('span', { class: 'order-no' }, row.orderNo),
  },
  {
    title: '状态',
    key: 'status',
    width: 140,
    render: (row) => h(StatusTag, { status: row.status, tone: TONE, size: 'sm' }),
  },
  { title: '客户', key: 'customer', width: 90, render: (row) => row.contact.name },
  { title: '路线', key: 'route', render: (row) => routeOf(row) },
  { title: '承运商', key: 'carrierName', width: 130, render: (row) => orDash(row.carrierName) },
  { title: '飞手', key: 'pilotName', width: 100, render: (row) => orDash(row.pilotName) },
  {
    title: '金额',
    key: 'paidAmount',
    width: 120,
    render: (row) => (row.paidAmount === null ? '未付' : `¥${formatCentsYuan(row.paidAmount)}`),
  },
  { title: '下单时间', key: 'createdAt', width: 120, render: (row) => shortTime(row.createdAt) },
  {
    title: '操作',
    key: 'actions',
    width: 80,
    render: (row) =>
      h(
        NButton,
        { text: true, type: 'primary', size: 'small', onClick: () => void openOrder(row.orderNo) },
        { default: () => '详情' },
      ),
  },
])

/**
 * 新字段卡那四行(画板 O1 的「这一轮补上的四个字段」)—— 四个字段契约里一直有
 * (OrderDetail.pilotName / refundStatus / refundAmount / deadRunFee),
 * 换版前运营端一个都没渲染。中文名各走单点表:退款状态走 refundStatusLabel
 * (四态:待退款 / 退款中 / 已退款 / 退款失败),金额一律 ¥ + 整数分。
 */
const newFields = computed(() => {
  const o = selectedOrder.value
  if (!o) return []
  return [
    { label: '承接飞手', value: orDash(o.pilotName) },
    { label: '退款状态', value: o.refundStatus === null ? '—' : refundStatusLabel(o.refundStatus) },
    { label: '退款金额', value: o.refundAmount === null ? '—' : `¥${formatCentsYuan(o.refundAmount)}` },
    { label: '空跑费', value: `¥${formatCentsYuan(o.deadRunFee)}` },
  ]
})

/**
 * 订单概览(画板 O1 的六行 + 换版前那三行)。
 *
 * 画板六行:客户 / 路线 / 计价版本 + 承运商 / 佣金率 / 实付。画板把「代理商」写作
 * 同一个实体的名字,而全仓一律叫承运商(契约、决策表、另一个端)——
 * 按 2026-10-16 的裁决统一取「承运商」,画板那个词登记在 design-copy 的 _notRequired 里。
 *
 * 另补三行(业务线 / 派单方式 / 下单时间):它们换版前就在这张卡上,画板没画不等于要丢 ——
 * 业务线还决定下面「代办推进」那张卡出不出现。
 */
const overviewFields = computed(() => {
  const o = selectedOrder.value
  if (!o) return []
  return [
    { label: '客户', value: o.contact.name },
    { label: '路线', value: routeOf(o) },
    { label: '计价版本', value: o.priceSnapshot.pricingTableVersion, mono: true },
    { label: '承运商', value: o.carrierName ?? '（还没有）' },
    {
      label: '佣金率',
      value: o.commissionRateBpSnapshot === null ? '（还没有）' : formatRateBp(o.commissionRateBpSnapshot),
    },
    { label: '实付', value: o.paidAmount === null ? '未付' : `¥${formatCentsYuan(o.paidAmount)}` },
    { label: '业务线', value: serviceTypeLabel(o.serviceType) },
    { label: '派单方式', value: dispatchModeLabel(o.dispatchMode) },
    { label: '下单时间', value: shortTime(o.createdAt) },
  ]
})

const contactFields = computed(() => {
  const o = selectedOrder.value
  if (!o) return []
  return [
    { label: '客户联系人', value: o.contact.name },
    { label: '联系电话', value: o.contact.phone, mono: true },
    { label: '实付', value: o.paidAmount === null ? '未付' : `¥${formatCentsYuan(o.paidAmount)}` },
    { label: '合计', value: `¥${formatCentsYuan(o.priceSnapshot.totalAmount)}` },
    {
      label: '合计构成',
      value:
        `运费 ¥${formatCentsYuan(o.priceSnapshot.freightFee)} + ` +
        `代办费 ¥${formatCentsYuan(o.priceSnapshot.agencyFee)}`,
      span: 2 as const,
    },
    {
      label: '距离 / 命中档',
      // 锚地内外走 shared 的标签表:原来是 `=== 'inside' ? '锚地内' : '锚地外'`,
      // 多出第三个取值时那个三元会把它显示成「锚地外」,而它看起来像一个正常的答案
      value: `${o.priceSnapshot.distanceKm} 公里 · ${o.priceSnapshot.distanceTier} 档 · ${anchorageScopeLabel(
        o.priceSnapshot.anchorageScope,
      )}`,
      span: 2 as const,
    },
  ]
})

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  loading.value = true
  try {
    const page = await api.listOrders({
      ...(statusFilter.value ? { status: statusFilter.value } : {}),
      ...(serviceTypeFilter.value ? { serviceType: serviceTypeFilter.value } : {}),
    })
    orders.value = page.items
    total.value = page.total
    lastError.value = null
    if (selectedOrderNo.value) await openOrder(selectedOrderNo.value, true)
  } catch (e) {
    show(e)
  } finally {
    loading.value = false
  }
}

/**
 * 统计卡的两个真数:各发一次**单状态**的轻请求(pageSize=1),只读 total。
 * 为什么不复用上面那页:上面那页被筛选控件改过,它的 total 是「当前筛选下的」,
 * 而这两张卡说的是「全部」—— 同一个数在两处各有一个口径时,演示时一定对不上。
 */
async function refreshCounts(): Promise<void> {
  try {
    const [pendingAccept, inTransit] = await Promise.all([
      api.listOrders({ status: 'pending_accept', pageSize: 1 }),
      api.listOrders({ status: 'in_transit', pageSize: 1 }),
    ])
    pendingAcceptTotal.value = pendingAccept.total
    inTransitTotal.value = inTransit.total
  } catch (e) {
    pendingAcceptTotal.value = null
    inTransitTotal.value = null
    show(e)
  }
}

async function openOrder(orderNo: string, silent = false): Promise<void> {
  // S-61:换了另一单才清草稿。同一单的 silent refresh(刷新后自动重开抽屉)
  // 必须保留已选承运商与备注 —— 无条件清会让运营填到一半的东西凭空消失。
  if (selectedOrderNo.value !== orderNo) {
    assignCarrierId.value = null
    note.value = ''
  }
  try {
    selectedOrderNo.value = orderNo
    selectedOrder.value = await api.getOrder(orderNo)
    orderAudit.value = (await api.listAuditLogs({ orderNo })).items
    openAuditIndex.value = null
    if (!silent) line(`已打开 ${orderNo}`)
  } catch (e) {
    selectedOrder.value = null
    orderAudit.value = []
    show(e)
  }
}

function line(s: string): void {
  message.info(s)
}

/** 代办推进:办理中 → 已办完 → 等接单。note 只进审计日志(客户看不到,M-01) */
async function advanceAgency(to: OrderStatus): Promise<void> {
  if (!selectedOrder.value) return
  try {
    const after = await api.advanceAgency(selectedOrder.value.orderNo, to, note.value.trim() || undefined)
    note.value = ''
    message.success(`${after.orderNo} → ${statusLabel(after.status)}`)
    await refresh()
    await refreshCounts()
  } catch (e) {
    show(e)
  }
}

/** 人工指派(B8):快照由服务端写,前端只挑一家 */
async function assign(): Promise<void> {
  if (!selectedOrder.value || !assignCarrierId.value) {
    message.warning('先选一家承运商')
    return
  }
  try {
    const after = await api.assignCarrier(selectedOrder.value.orderNo, assignCarrierId.value)
    // 契约允许快照为 null —— 不许用 ?? 0 把它显示成「0%」(那是一个看起来正常的费率)。
    // 与同页订单概览对 null 的说法一致:还没有就是还没有。
    const rateText =
      after.commissionRateBpSnapshot === null
        ? '佣金率快照未写入（服务端没有返回）'
        : `佣金率快照 ${formatRateBp(after.commissionRateBpSnapshot)}`
    message.success(`已指派给 ${after.carrierName};${rateText}`)
    await refresh()
    await refreshCounts()
  } catch (e) {
    show(e)
  }
}

/** 审计流水的明细:只在那一行有键可展开时才给按钮(空对象展开是一片 `{}`) */
function hasAuditDetail(a: AuditLogItem): boolean {
  return Object.keys(a.detail ?? {}).length > 0
}

function toggleAuditDetail(index: number): void {
  openAuditIndex.value = openAuditIndex.value === index ? null : index
}

// 今日新增:契约里没有数据源 —— opsListOrders 的参数只有 page / pageSize / status / serviceType,
// 没有 createdAt 区间,所以「今天这一天新下的单」这个数今天算不出来。画「—」。
// 口径与 AS-079 / AS-080 相同:**不许编数**,也不许拿「本页里今天那几条」顶替
// (那是把一页 50 条里的计数说成全部)。
// @unimplemented(reason="今日新增需要按创建日期的区间过滤,而契约 opsListOrders 没有 createdFrom/createdTo(AS-079 / AS-080 的同一口径:拿不到真数就画「—」,不许编数)。补它要改契约 + 服务端 where,不在本切片范围", owner="me", blocks="O1 统计卡「今日新增」")

// 待审承运商:入驻申请队列(GET /api/admin/carrier-applications)只有 page / pageSize,
// 没有 status 过滤,也没有计数接口 —— 列表回的是四个字段 + total,而 total 是**全部**申请数。
// 「待审」这个数同样拿不到;不许数页内条数、也不许拿申请总数顶替。画「—」。
// @unimplemented(reason="待审承运商需要按状态过滤或一个计数接口,而契约 opsListCarrierApplications 只有 page/pageSize(返回体的 total 是全部申请数,不是待审数)。要么给队列加 status 参数,要么加一个计数接口 —— 两条都要先改契约", owner="me", blocks="O1 统计卡「待审承运商」")

onMounted(async () => {
  await Promise.all([refresh(), refreshCounts()])
  try {
    carriers.value = (await api.listCarriers()).items
  } catch (e) {
    show(e)
  }
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="订单驾驶舱" :notes="NOTES">
      <template #actions>
        <n-button text class="refresh" :loading="loading" @click="refresh">
          <template #icon>
            <NavIcon name="refresh" :size="14" />
          </template>
          刷新
        </n-button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!-- 四张统计卡。今日新增与待审承运商在契约里没有数据源,画「—」—— 见文件头那两条 @unimplemented -->
    <div class="stats">
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="今日新增" value="—" tone="brand" />
      <StatTile
        theme="ops-dark"
        bar="right"
        :watermark="false"
        label="待指配"
        :value="pendingAcceptTotal ?? '—'"
        tone="warning"
      />
      <StatTile
        theme="ops-dark"
        bar="right"
        :watermark="false"
        label="在途"
        :value="inTransitTotal ?? '—'"
        tone="brand"
      />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="待审承运商" value="—" tone="warning" />
    </div>

    <!-- 筛选条:只留契约真有的参数。「全部承运商」「近 7 天」没有对应参数,不渲染(见文件头) -->
    <PanelCard :tone="TONE" padding="none">
      <div class="filters">
        <n-select
          v-model:value="statusFilter"
          :options="statusOptions"
          clearable
          placeholder="全部状态"
          class="sel"
        />
        <n-select
          v-model:value="serviceTypeFilter"
          :options="serviceTypeOptions"
          clearable
          placeholder="全部业务线"
          class="sel"
        />
        <span class="grow" />
        <span class="count">共 {{ total }} 单</span>
      </div>
    </PanelCard>

    <PanelCard :tone="TONE" padding="md">
      <DataTable
        :tone="TONE"
        :columns="columns"
        :data="orders"
        :row-key="(row: AdminOrderDetail) => row.orderNo"
        :loading="loading"
        empty-text="没有符合条件的订单"
      />
    </PanelCard>

    <n-drawer v-model:show="drawerOpen" :width="760" placement="right">
      <n-drawer-content v-if="selectedOrder" :native-scrollbar="false" closable>
        <template #header>
          <div class="drawer-head">
            <div class="drawer-head-main">
              <span class="drawer-title">订单详情</span>
              <span class="drawer-sub">{{ selectedOrder.orderNo }} · {{ statusLabel(selectedOrder.status) }}</span>
            </div>
            <!-- 完整页入口(O9):抽屉里没有凭证照片与航线示意,那两块的落点是那一页 -->
            <button type="button" class="open-detail" @click="openFullDetail(selectedOrder.orderNo)">
              查看完整详情 ›
            </button>
          </div>
        </template>

        <div class="drawer-body">
          <!--
            ① 新字段卡:画板 O1 的「这一轮补上的四个字段」。它那张卡的标题与说明字是
            设计轮写给实现者的注记(不是产品会对运营说的话),按 design-copy 的 _notRequired
            不渲染;高亮底与两列行布局照画板留 —— 底用的是语义 info 的浅底
            (深色档 = rgba(77,139,245,.12),与画板 $gov-tag-active-bg #4D8BF51F 逐值相同,
            不另开一个「差不多的蓝」)
          -->
          <section class="new-fields">
            <FieldGrid :tone="TONE" inline :items="newFields" />
          </section>

          <PanelCard :tone="TONE" title="订单概览">
            <FieldGrid :tone="TONE" :items="overviewFields" />
          </PanelCard>

          <PanelCard :tone="TONE" title="审计流水">
            <ol v-if="orderAudit.length > 0" class="audit">
              <li v-for="(a, i) in orderAudit" :key="`${a.occurredAt}-${i}`" class="audit-row">
                <span class="audit-dot" aria-hidden="true" />
                <span class="audit-time">{{ shortTime(a.occurredAt) }}</span>
                <!-- 事件名渲染**英文原码**:AS-035 已裁(它是取证视图的一部分,译成中文会多一次人工反查) -->
                <span class="audit-event">{{ a.eventType }}</span>
                <span v-if="a.actorId" class="audit-actor">{{ a.actorId }}</span>
                <button
                  v-if="hasAuditDetail(a)"
                  type="button"
                  class="audit-toggle"
                  @click="toggleAuditDetail(i)"
                >
                  {{ openAuditIndex === i ? '收起明细' : '明细' }}
                </button>
                <pre v-if="openAuditIndex === i" class="audit-json">{{ JSON.stringify(a.detail, null, 2) }}</pre>
              </li>
            </ol>
            <EmptyState v-else :tone="TONE" text="这一单还没有任何事件" />
          </PanelCard>

          <!-- ② 以下四张画板没画:S-10 的能力与换版前就在的信息,删掉它们等于把验收链路摘掉 -->
          <PanelCard :tone="TONE" title="客户联系人与金额" :notes="MONEY_NOTES">
            <FieldGrid :tone="TONE" :items="contactFields" />
          </PanelCard>

          <PanelCard :tone="TONE" title="运输段">
            <ol v-if="selectedOrder.legs.length > 0" class="legs">
              <li v-for="leg in selectedOrder.legs" :key="leg.legNo" class="leg">
                <span class="leg-no">#{{ leg.legNo }}</span>
                <span class="leg-mode">{{ transportModeLabel(leg.mode) }}</span>
                <StatusTag :status="leg.status" :tone="TONE" size="sm" />
              </li>
            </ol>
            <EmptyState v-else :tone="TONE" text="这一单还没有运输段" />
          </PanelCard>

          <PanelCard
            v-if="selectedOrder.serviceType === 'full_agency'"
            :tone="TONE"
            title="代办推进"
            :notes="AGENCY_NOTES"
          >
            <n-space vertical>
              <n-input v-model:value="note" placeholder="审批备注（可选，只进审计日志）" />
              <n-space>
                <n-button
                  size="small"
                  type="primary"
                  :disabled="selectedOrder.status !== 'agency_processing'"
                  @click="advanceAgency('agency_done')"
                >
                  已办完
                </n-button>
                <n-button
                  size="small"
                  type="primary"
                  :disabled="selectedOrder.status !== 'agency_done'"
                  @click="advanceAgency('pending_accept')"
                >
                  转「等接单」（进订单池）
                </n-button>
              </n-space>
            </n-space>
          </PanelCard>

          <!-- 标题只留运营当场要判断的那半句(什么时候用这张卡);「B8 兜底」的依据在 ASSIGN_NOTES 里 -->
          <PanelCard :tone="TONE" title="人工指派（无人抢单时用）" :notes="ASSIGN_NOTES">
            <n-space vertical>
              <n-select
                v-model:value="assignCarrierId"
                :options="carrierOptions"
                placeholder="选一家启用的承运商"
              />
              <n-space>
                <n-button
                  size="small"
                  type="primary"
                  :disabled="selectedOrder.status !== 'pending_accept'"
                  @click="assign"
                >
                  指派
                </n-button>
              </n-space>
            </n-space>
          </PanelCard>
        </div>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

/* 刷新:画板的右端那枚「图标 + 字」的可见按钮,无底色无描边(naive 的 text 型按钮正好) */
.refresh {
  color: var(--sd-text2);
}

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.sel {
  width: 140px;
}

.grow {
  flex: 1;
}

.count {
  font-size: 13px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ── 抽屉 ───────────────────────────────────────────── */

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.drawer-head-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

/* 完整页入口:一枚真链接样子的文字键(点它换路由,不是抽屉里的锚点) */
.open-detail {
  flex: none;
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 13px;
  color: var(--sd-brand);
  cursor: pointer;
  white-space: nowrap;
}

.open-detail:hover {
  text-decoration: underline;
}

.drawer-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--sd-text1);
}

.drawer-sub {
  font-size: 13px;
  color: var(--sd-text3);
}

.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 新字段卡的高亮底:画板那一张卡的底是 $gov-tag-active-bg(#4D8BF51F)——
   深色档的语义 info 浅底与它逐值相同,所以引用 --sd-info-bg 而不是就地写一个色值 */
.new-fields {
  padding: 16px;
  border-radius: var(--sd-radius-lg);
  background: var(--sd-info-bg);
}

.legs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.leg {
  display: flex;
  align-items: center;
  gap: 12px;
}

.leg-no {
  font-family: var(--sd-font-mono);
  font-size: 13px;
  color: var(--sd-text2);
}

.leg-mode {
  font-size: 13px;
  color: var(--sd-text1);
}

/* ── 审计流水 ───────────────────────────────────────── */

.audit {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.audit-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
}

/* 6×6 的圆点:画板那一行的行首标记 */
.audit-dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-brand);
}

.audit-time {
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}

.audit-event {
  color: var(--sd-text1);
}

.audit-actor {
  font-family: var(--sd-font-mono);
  font-size: 12px;
  color: var(--sd-text3);
}

.audit-toggle {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: var(--sd-brand);
  cursor: pointer;
}

.audit-json {
  flex-basis: 100%;
  margin: 0;
  font-family: var(--sd-font-mono);
  font-size: 12px;
  line-height: 1.6;
  color: var(--sd-text2);
  white-space: pre-wrap;
  word-break: break-all;
}

/* 表格单元格是 DataTable 渲染出来的,拿不到本组件的 scope 属性 —— 用 :deep 穿进去 */
:deep(.order-no) {
  font-family: var(--sd-font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
}

@media (max-width: 1200px) {
  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .stats {
    grid-template-columns: 1fr;
  }
}
</style>
